import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  useProgramId,
  useProgramTheme,
  useVerificationId,
  useVerificationResponse,
} from "@/lib/state/contextHooks";
import { isDocUploadResponse, isPendingResponse } from "@/lib/types/assertions";
import { type Locale } from "@/lib/types/types";

export type IntlValueId = keyof ReturnType<typeof useIntlValues>;

export function useIntlValues() {
  const { i18n, t } = useTranslation();
  const programId = useProgramId();
  const programTheme = useProgramTheme();
  const verificationResponse = useVerificationResponse();
  const verificationId = useVerificationId();
  const locale = i18n.language as Locale;

  const isReviewStep =
    isPendingResponse(verificationResponse) || isDocUploadResponse(verificationResponse);

  const estimatedReviewTime =
    (isReviewStep && verificationResponse.estimatedReviewTime) ||
    programTheme?.config?.estimatedReviewTime ||
    "A_FEW_MINUTES";

  const maxReviewTime =
    (isReviewStep && verificationResponse.maxReviewTime) ||
    programTheme?.config?.maxReviewTime ||
    "2_HRS";

  return useMemo(
    () => ({
      programId,
      verificationId,
      locale,
      companyName: t("companyName"),
      estimatedReviewTime: t(`dateTime.${estimatedReviewTime}`, estimatedReviewTime),
      maxReviewTime: t(`dateTime.${maxReviewTime}`, maxReviewTime),
    }),
    [programId, verificationId, locale, estimatedReviewTime, maxReviewTime, t],
  );
}
